// 有開始 / 結束的事件配成「同一次」（VMS UX 評審第八輪 R8-P0-1、R8-P1-1，B8-01、B8-02；B7-17 資料確認，SPEC #vms-web-290 第八輪收尾）：
//   - 同一次移動 = 同一個 device_ref＋同一個 binding_id＋同一個事件 key，phase 嚴格 started → ended 交替
//   - 開始那筆配「它之後第一個結束」，結束那筆配「它之前第一個開始」；中間夾了另一個開始 / 結束就不跨過去（不配到上一次）
//   - phase 讀事件本身（搜尋列 / façade 的 phase 欄，沒有才看 payload.phase；舊的 start / end 也認）
// 清單（事件與警報、攝影機事件分頁）與事件詳細用同一個函式。

export type EpPhase = 'started' | 'ended';

/** 事件的開始 / 結束；單次（instant）或認不出來 = undefined。 */
export function phaseOf(x: { phase?: unknown; payload?: Record<string, unknown> | null; fields?: Record<string, unknown> | null }): EpPhase | undefined {
  const raw = typeof x.phase === 'string' && x.phase ? x.phase : (x.payload?.phase ?? x.fields?.phase);
  if (raw === 'started' || raw === 'start') return 'started';
  if (raw === 'ended' || raw === 'end') return 'ended';
  return undefined;
}

export interface EpItem {
  id: string;
  /** 發生時間（ms） */
  ts: number;
  phase?: EpPhase;
  /** device_ref（通道 = <parent>:chN） */
  ref: string;
  binding?: string;
  key: string;
}

export interface Episode {
  start?: EpItem;
  end?: EpItem;
}

/** 同一筆在兩個來源（索引與串流）時間差幾秒：同一個 phase、相隔 3 秒內算同一筆。 */
const DUP_MS = 3000;
/** 開始到結束最長多久還算同一次（超過就不配；照攝影機事件分頁的 24 小時）。 */
const MAX_SPAN_MS = 24 * 3600_000;

const groupOf = (x: EpItem) => `${x.ref}|${x.binding ?? ''}|${x.key}`;

/**
 * 沒帶 binding_id 的（串流插進來的即時事件）當成同一台、同一個 key 的那個 binding_id（第九輪 B9-02：即時的「結束」
 * 沒有 binding_id，配不到清單上已有的「進行中」）。同一台同一個 key 有好幾個 binding_id 時，取時間上最近的前一筆（沒有就最近的後一筆）。
 */
export function fillBindings(items: EpItem[]): EpItem[] {
  if (!items.some((x) => x.phase && !x.binding)) return items;
  const known = new Map<string, EpItem[]>();
  for (const x of items) {
    if (!x.phase || !x.binding) continue;
    const k = `${x.ref}|${x.key}`;
    const l = known.get(k);
    if (l) l.push(x);
    else known.set(k, [x]);
  }
  return items.map((x) => {
    if (!x.phase || x.binding) return x;
    const l = known.get(`${x.ref}|${x.key}`);
    if (!l?.length) return x;
    let before: EpItem | undefined;
    let after: EpItem | undefined;
    for (const y of l) {
      if (y.ts <= x.ts) {
        if (!before || y.ts > before.ts) before = y;
      } else if (!after || y.ts < after.ts) after = y;
    }
    const b = (before ?? after)!.binding;
    return { ...x, binding: b };
  });
}

/**
 * 整份清單配對：回傳「每一筆 id → 它所在的那一次」。沒有 phase 的不在結果裡。
 * 清單順序不拘（裡面依時間排）。
 */
export function pairEpisodes(items: EpItem[]): Map<string, Episode> {
  const out = new Map<string, Episode>();
  const groups = new Map<string, EpItem[]>();
  for (const x of fillBindings(items)) {
    if (!x.phase) continue;
    const g = groupOf(x);
    const l = groups.get(g);
    if (l) l.push(x);
    else groups.set(g, [x]);
  }
  for (const list of groups.values()) {
    // 同一毫秒：開始排在結束前面
    list.sort((a, b) => a.ts - b.ts || (a.phase === b.phase ? 0 : a.phase === 'started' ? -1 : 1));
    let open: Episode | null = null; // 開始了、還沒配到結束
    let last: Episode | null = null; // 上一次（給重複的結束用）
    for (const x of list) {
      if (x.phase === 'started') {
        if (open?.start && x.ts - open.start.ts <= DUP_MS) {
          out.set(x.id, open); // 同一筆開始的另一份
          continue;
        }
        open = { start: x };
        last = open;
        out.set(x.id, open);
      } else {
        if (open?.start && x.ts - open.start.ts <= MAX_SPAN_MS) {
          open.end = x;
          out.set(x.id, open);
          open = null;
          continue;
        }
        if (!open && last?.end && x.ts - last.end.ts <= DUP_MS) {
          out.set(x.id, last); // 同一筆結束的另一份
          continue;
        }
        open = null;
        last = { end: x };
        out.set(x.id, last);
      }
    }
  }
  return out;
}

/** 開始超過這麼久還沒有結束紀錄，就不當「進行中」（10 分鐘）。 */
export const EPISODE_STALE_MS = 600_000;

/**
 * 清單合成一列（R8-P1-1）：同一次的開始＋結束只留開始那筆（列上的時間 = 開始時間），結束那筆藏起來；
 * 只有結束（開始在下一頁或範圍外）、只有開始（還沒結束）照留。回傳順序同輸入。
 */
export function mergeEpisodeRows<R>(rows: R[], item: (r: R) => EpItem | null): { r: R; ep?: Episode }[] {
  const items: EpItem[] = [];
  const byRow = new Map<R, EpItem>();
  for (const r of rows) {
    const it = item(r);
    if (it?.phase) {
      items.push(it);
      byRow.set(r, it);
    }
  }
  if (!items.length) return rows.map((r) => ({ r }));
  const eps = pairEpisodes(items);
  const out: { r: R; ep?: Episode }[] = [];
  for (const r of rows) {
    const it = byRow.get(r);
    if (!it) {
      out.push({ r });
      continue;
    }
    const ep = eps.get(it.id);
    if (!ep) {
      out.push({ r });
      continue;
    }
    // 留下來的那一列：有開始就是開始那筆，否則結束那筆；同一次的其他份（結束、重複）藏起來
    const head = ep.start ?? ep.end;
    if (head && head.id !== it.id) continue;
    out.push({ r, ep });
  }
  return out;
}

/** 一次的狀態：持續秒數；進行中；開始後超過 10 分鐘沒有結束紀錄；只有結束。 */
export function episodeState(ep: Episode, now = Date.now()): { kind: 'done'; sec: number } | { kind: 'open' } | { kind: 'stale' } | { kind: 'end_only' } {
  if (ep.start && ep.end) return { kind: 'done', sec: Math.max(0, Math.round((ep.end.ts - ep.start.ts) / 1000)) };
  if (ep.start) return now - ep.start.ts > EPISODE_STALE_MS ? { kind: 'stale' } : { kind: 'open' };
  return { kind: 'end_only' };
}
